/**
 * SSE-подписки: события подключения провайдеров и статуса медиа.
 * Вынесена общая логика чтения SSE-потока в parseSSEStream.
 */
import { getRealm } from '../lib/realm.js';
import { API_BASE, getToken, redirectToLogin } from './client.js';

/**
 * Читает SSE-поток и вызывает onMessage для каждого data-события.
 * Возвращает reader для возможности отмены.
 */
function parseSSEStream(reader, onMessage, onDone) {
  const decoder = new TextDecoder();
  let buf = '';

  function read() {
    reader
      .read()
      .then(({ done, value }) => {
        if (done) {
          onDone?.();
          return;
        }
        buf += decoder.decode(value, { stream: true });
        const blocks = buf.split(/\n\n/);
        buf = blocks.pop() || '';
        for (const block of blocks) {
          const line = block.split('\n').find((l) => l.startsWith('data: '));
          if (line) {
            try {
              onMessage(JSON.parse(line.slice(6)));
            } catch (_) {}
          }
        }
        read();
      })
      .catch(() => onDone?.());
  }

  read();
}

/**
 * Подписка на SSE: изменение статуса подключения провайдеров (все модели).
 * Возвращает функцию отмены.
 */
export function subscribeConnectionStatus(onUpdate) {
  const token = getToken(getRealm());
  if (!token) return () => {};

  const controller = new AbortController();

  fetch(`${API_BASE}/admin/connection-status/stream`, {
    headers: { Authorization: `Bearer ${token}` },
    signal: controller.signal,
  })
    .then((res) => {
      if (res.status === 401) return redirectToLogin(getRealm());
      if (!res.body || !res.ok) return;
      parseSSEStream(res.body.getReader(), onUpdate);
    })
    .catch(() => {});

  return () => controller.abort();
}

/**
 * Подписка на SSE: события чатов (новые чаты, сообщения, логи).
 * При обрыве переподключается. Возвращает функцию отмены.
 */
export function subscribeChatEvents(onUpdate) {
  const token = getToken(getRealm());
  if (!token) return () => {};

  let aborted = false;
  const controller = new AbortController();

  function connect() {
    if (aborted) return;
    fetch(`${API_BASE}/admin/chat-events/stream`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then((res) => {
        if (aborted) return;
        if (res.status === 401) return redirectToLogin(getRealm());
        if (!res.body || !res.ok) return;
        parseSSEStream(
          res.body.getReader(),
          onUpdate,
          () => { if (!aborted) setTimeout(connect, 3000); }
        );
      })
      .catch(() => {
        if (!aborted) setTimeout(connect, 3000);
      });
  }

  connect();
  return () => {
    aborted = true;
    controller.abort();
  };
}

/**
 * Подписка на SSE: изменение статуса медиа конкретной модели.
 * При обрыве переподключается. Возвращает функцию отмены.
 */
export function subscribeMediaStatus(modelId, onUpdate) {
  const token = getToken(getRealm());
  if (!token || !modelId) return () => {};

  let aborted = false;
  const controller = new AbortController();
  const url = `${API_BASE}/admin/media-status/stream?model_id=${encodeURIComponent(modelId)}`;

  function connect() {
    if (aborted) return;
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then((res) => {
        if (aborted) return;
        if (res.status === 401) return redirectToLogin(getRealm());
        if (!res.body || !res.ok) return;
        parseSSEStream(
          res.body.getReader(),
          onUpdate,
          () => { if (!aborted) setTimeout(connect, 3000); }
        );
      })
      .catch(() => {
        if (!aborted) setTimeout(connect, 3000);
      });
  }

  connect();
  return () => {
    aborted = true;
    controller.abort();
  };
}
